import { Component, inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { SeoService } from '../../core/services/seo.service';
import { GewerkService } from '../../core/services/gewerk.service';
import { HandwerkerService } from '../../core/services/handwerker.service';
import { PlacesService, PlaceSuggestion, PlaceDetails } from '../../core/services/places.service';
import { GewerkDto } from '../../core/models/gewerk.model';
import { gewerkIdToTradeKey, findGewerkByText } from '../../core/data/gewerk-trade-key';
import { HandwerkerSucheDto } from '../../core/models/handwerker-suche.model';
import { KategorieGridComponent } from '../../shared/components/kategorie-grid/kategorie-grid.component';
import { HandwerkerKarteComponent } from '../../shared/components/handwerker-karte/handwerker-karte.component';
import { SuchtypModalComponent } from '../../shared/components/suchtyp-modal/suchtyp-modal.component';
import { GewerkIconComponent } from '../../shared/components/gewerk-icon/gewerk-icon.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [FormsModule, RouterLink, KategorieGridComponent, HandwerkerKarteComponent, SuchtypModalComponent, GewerkIconComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly gewerkService = inject(GewerkService);
  private readonly handwerkerService = inject(HandwerkerService);
  private readonly placesService = inject(PlacesService);
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly destroy$ = new Subject<void>();
  private readonly ortSearch$ = new Subject<string>();

  gewerke: GewerkDto[] = [];
  naheHandwerker: HandwerkerSucheDto[] = [];
  naheLoading = true;

  /** City label for the "In deiner Nähe · X" header. Null = no location → fallback label. */
  ortLabel: string | null = null;
  /**
   * Geolocation lifecycle:
   * - 'idle': not asked yet (SSR or before browser bootstrap)
   * - 'pending': prompt visible / browser is resolving the position
   * - 'granted': we have lat/lng
   * - 'denied': user rejected, browser blocked, or timeout/error
   * - 'unsupported': no navigator.geolocation
   */
  geoState: 'idle' | 'pending' | 'granted' | 'denied' | 'unsupported' = 'idle';

  /** Selected gewerk that drives the "in deiner Nähe" section (defaults to first available). */
  private naheGewerkId: string | null = null;
  private static readonly GEO_CACHE_KEY = 'craftly_geo_v1';
  private static readonly GEO_CACHE_TTL_MS = 30 * 60_000;

  // Gewerk autocomplete
  gewerkInput = '';
  selectedGewerk: GewerkDto | null = null;
  filteredGewerke: GewerkDto[] = [];
  gewerkDropdownOpen = false;

  // Ort autocomplete (Mapbox place mode → cities, postcodes, regions)
  ortInput = '';
  ortSuggestions: PlaceSuggestion[] = [];
  ortDropdownOpen = false;
  /** Vom Nutzer gewählter Ort inkl. Koordinaten — wird an /suche durchgereicht. */
  private selectedOrt: PlaceDetails | null = null;
  /** Mapbox-Session-Token für die laufende Eingabe. Suggest+Retrieve mit
   * gleichem Token = 1 Billing-Event. Wird nach jedem Retrieve rotiert. */
  private ortSessionToken = '';

  // Suchtyp modal
  showSuchtypModal = false;

  ngOnInit(): void {
    this.seo.apply({
      title: 'Craftly – Handwerker finden & online buchen',
      description:
        'Craftly verbindet dich mit geprüften Handwerkern in deiner Nähe. Jetzt kostenlos Handwerker finden und Termin online buchen.',
      path: '/',
    });

    this.gewerkService.getAll().subscribe({
      next: (data) => {
        this.gewerke = data;
        this.filteredGewerke = data;
        if (data.length > 0) {
          this.naheGewerkId = data[0].id;
          this.loadNaheHandwerker(data[0].id);
          this.tryUseGeolocation();
        } else {
          this.naheLoading = false;
        }
      },
      error: () => { this.naheLoading = false; },
    });

    if (isPlatformBrowser(this.platformId)) {
      this.ortSearch$.pipe(
        debounceTime(300),
        switchMap((query) => {
          if (!query.trim() || query.length < 2) return of<PlaceSuggestion[]>([]);
          if (!this.ortSessionToken) this.ortSessionToken = this.placesService.newSessionToken();
          return this.placesService.suggest(query, 'place', this.ortSessionToken);
        }),
        takeUntil(this.destroy$),
      ).subscribe((suggestions) => {
        this.ortSuggestions = suggestions;
        this.ortDropdownOpen = suggestions.length > 0;
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Gewerk autocomplete ---

  onGewerkInput(): void {
    const val = this.gewerkInput.toLowerCase().trim();
    this.filteredGewerke = val
      ? this.gewerke.filter((g) => g.label.toLowerCase().includes(val))
      : this.gewerke;
    this.gewerkDropdownOpen = true;
    this.selectedGewerk = null;
  }

  onGewerkFocus(): void {
    if (!this.gewerkInput) this.filteredGewerke = this.gewerke;
    this.gewerkDropdownOpen = true;
  }

  onGewerkBlur(): void {
    setTimeout(() => { this.gewerkDropdownOpen = false; }, 150);
  }

  selectGewerk(g: GewerkDto): void {
    this.selectedGewerk = g;
    this.gewerkInput = g.label;
    this.gewerkDropdownOpen = false;
  }

  // --- Ort autocomplete ---

  onOrtInput(): void {
    // Sobald der Nutzer weiter tippt ist die alte Auswahl ungültig.
    this.selectedOrt = null;
    this.ortSuggestions = [];
    this.ortDropdownOpen = false;
    this.ortSearch$.next(this.ortInput);
  }

  onOrtFocus(): void {
    if (this.ortSuggestions.length > 0) this.ortDropdownOpen = true;
  }

  onOrtBlur(): void {
    setTimeout(() => { this.ortDropdownOpen = false; }, 150);
  }

  selectOrt(s: PlaceSuggestion): void {
    // Sofort den Text ins Feld übernehmen — Retrieve läuft im Hintergrund
    // und liefert nur die Koordinaten nach.
    this.ortInput = PlacesService.shortLabel(s);
    this.ortDropdownOpen = false;
    this.ortSuggestions = [];

    this.placesService.retrieve(s.mapboxId, this.ortSessionToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe((details) => {
        // Token nach Retrieve verbrennen — nächster Eingabevorgang bekommt einen frischen.
        this.ortSessionToken = '';
        if (details) this.selectedOrt = details;
      });
  }

  // --- Form submit ---

  /** Resolved Gewerk id (PascalCase, e.g. "Heizung", "TransportUmzug"). */
  private resolveGewerkId(): string | null {
    if (this.selectedGewerk) return this.selectedGewerk.id;
    const match = findGewerkByText(this.gewerkInput, this.gewerke);
    return match?.id ?? null;
  }

  /** Trigger the search-type chooser instead of jumping straight to /suche. */
  onSuche(): void {
    this.showSuchtypModal = true;
  }

  onSuchtypClosed(): void {
    this.showSuchtypModal = false;
  }

  /** Card B: classic browse experience. */
  onSuchtypSelf(): void {
    this.showSuchtypModal = false;
    const params: Record<string, string | number> = {};
    const gewerkId = this.resolveGewerkId();
    if (gewerkId) params['gewerk'] = gewerkId;
    this.appendOrtParams(params);

    this.router.navigate(['/suche'], {
      queryParams: Object.keys(params).length ? params : undefined,
    });
  }

  /** Card A: guided wizard. Pass trade slug if we resolved one. */
  onSuchtypGuided(): void {
    this.showSuchtypModal = false;
    const gewerkId = this.resolveGewerkId();
    const queryParams = gewerkId ? { trade: gewerkIdToTradeKey(gewerkId) } : undefined;
    this.router.navigate(['/anfrage/neu'], { queryParams });
  }

  /**
   * Reicht den Ort an /suche weiter. Wenn der Nutzer einen Vorschlag gewählt
   * hat, hängen wir lat/lng dran damit /suche kein zweites Geocoding macht.
   */
  private appendOrtParams(params: Record<string, string | number>): void {
    if (this.selectedOrt) {
      params['ort'] = PlacesService.shortLabel(this.selectedOrt);
      params['lat'] = this.selectedOrt.lat;
      params['lng'] = this.selectedOrt.lng;
    } else if (this.ortInput.trim()) {
      params['ort'] = this.ortInput.trim();
    }
  }

  private loadNaheHandwerker(gewerk: string, lat?: number, lng?: number): void {
    this.naheLoading = true;
    const params = lat != null && lng != null
      ? { gewerk, proSeite: 4, lat, lng, radiusKm: 50 }
      : { gewerk, proSeite: 4 };
    this.handwerkerService.suche(params).subscribe({
      next: (r) => { this.naheHandwerker = r.items; this.naheLoading = false; },
      error: () => { this.naheLoading = false; },
    });
  }

  // ── Geolocation ─────────────────────────────────────────────────────────

  /**
   * Resolves the user's coordinates and refines the "In deiner Nähe" feed.
   *
   * Flow: cached position (sessionStorage, 30 min TTL) → navigator.geolocation
   * → reverse geocode → re-query handwerker with lat/lng + 50 km radius. On
   * denial, error, or unsupported browser, the fallback label kicks in
   * ("Beliebte Betriebe") and the unfiltered feed stays as-is. Users can
   * retry via the manual button.
   */
  tryUseGeolocation(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const cached = this.readCachedPosition();
    if (cached) {
      this.geoState = 'granted';
      this.ortLabel = cached.ortLabel;
      if (this.naheGewerkId) this.loadNaheHandwerker(this.naheGewerkId, cached.lat, cached.lng);
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.geoState = 'unsupported';
      return;
    }

    this.geoState = 'pending';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        this.placesService.reverseGeocode(lat, lng).subscribe((details) => {
          this.geoState = 'granted';
          this.ortLabel = details ? PlacesService.shortLabel(details) : null;
          this.cachePosition(lat, lng, this.ortLabel);
          if (this.naheGewerkId) this.loadNaheHandwerker(this.naheGewerkId, lat, lng);
        });
      },
      () => { this.geoState = 'denied'; },
      { timeout: 8000, maximumAge: HomeComponent.GEO_CACHE_TTL_MS },
    );
  }

  private readCachedPosition(): { lat: number; lng: number; ortLabel: string | null } | null {
    try {
      const raw = sessionStorage.getItem(HomeComponent.GEO_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { lat: number; lng: number; ortLabel: string | null; ts: number };
      if (Date.now() - parsed.ts > HomeComponent.GEO_CACHE_TTL_MS) return null;
      return { lat: parsed.lat, lng: parsed.lng, ortLabel: parsed.ortLabel };
    } catch {
      return null;
    }
  }

  private cachePosition(lat: number, lng: number, ortLabel: string | null): void {
    try {
      sessionStorage.setItem(
        HomeComponent.GEO_CACHE_KEY,
        JSON.stringify({ lat, lng, ortLabel, ts: Date.now() }),
      );
    } catch { /* quota/private-mode: silently skip */ }
  }

  onKategorieSelected(gewerk: GewerkDto): void {
    this.selectedGewerk = gewerk;
    this.gewerkInput = gewerk.label;
    this.showSuchtypModal = true;
  }

  /** "Alle Gewerke →"-Kachel: direkt in die Suche, ohne Gewerk-Vorauswahl. */
  onAlleGewerke(): void {
    this.router.navigate(['/suche']);
  }
}
